import { useState, useEffect } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Filter,
  Users,
  Loader2,
  Eye
} from 'lucide-react';

import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { getAuthHeaders, adminFetch } from '../../../services/adminApi';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const MEMBERSHIP_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'past_due', label: 'Past Due' },
  { value: 'none', label: 'No Subscription' },
];

const ACCOUNT_TYPES = [
  { value: 'individual', label: 'Individual (Adult Student)' },
  { value: 'parent', label: 'Parent (Manages Minor Students)' },
];

export default function NewsletterSegmentsTab({ showMessage }) {
  const [segments, setSegments] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [belts, setBelts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    criteria: {
      programs: [],
      programs_exclude: [],
      belt_min: '',
      belt_max: '',
      attendance_min: null,
      attendance_max: null,
      membership_status: [],
      member_since_days_min: null,
      member_since_days_max: null,
      account_types: [],
    }
  });

  useEffect(() => {
    fetchSegments();
    fetchPrograms();
    fetchBelts();
  }, []);

  const fetchSegments = async () => {
    setLoading(true);
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/segments`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSegments(data);
      }
    } catch (error) {
      console.error('Error fetching segments:', error);
      showMessage('Failed to load segments', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await adminFetch(`${API_BASE}/api/programs`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPrograms(data);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  const fetchBelts = async () => {
    try {
      const response = await adminFetch(`${API_BASE}/api/belts`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setBelts(data);
      }
    } catch (error) {
      console.error('Error fetching belts:', error);
    }
  };

  const handleCreate = () => {
    setEditing(null);
    setFormData({
      name: '',
      description: '',
      criteria: {
        programs: [],
        programs_exclude: [],
        belt_min: '',
        belt_max: '',
        attendance_min: null,
        attendance_max: null,
        membership_status: [],
        member_since_days_min: null,
        member_since_days_max: null,
        account_types: [],
      }
    });
    setPreviewData(null);
    setShowEditor(true);
  };

  const handleEdit = (segment) => {
    setEditing(segment);
    setFormData({
      name: segment.name,
      description: segment.description || '',
      criteria: {
        programs: segment.criteria?.programs || [],
        programs_exclude: segment.criteria?.programs_exclude || [],
        belt_min: segment.criteria?.belt_min || '',
        belt_max: segment.criteria?.belt_max || '',
        attendance_min: segment.criteria?.attendance_min,
        attendance_max: segment.criteria?.attendance_max,
        membership_status: segment.criteria?.membership_status || [],
        member_since_days_min: segment.criteria?.member_since_days_min,
        member_since_days_max: segment.criteria?.member_since_days_max,
        account_types: segment.criteria?.account_types || [],
      }
    });
    setPreviewData(null);
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showMessage('Segment name is required', 'error');
      return;
    }

    setSaving(true);
    try {
      const url = editing
        ? `${API_BASE}/api/admin/newsletter/segments/${editing.id}`
        : `${API_BASE}/api/admin/newsletter/segments`;
      const method = editing ? 'PUT' : 'POST';

      // Clean up criteria - remove empty values
      const cleanCriteria = { ...formData.criteria };
      if (cleanCriteria.attendance_min === '' || cleanCriteria.attendance_min === null) {
        cleanCriteria.attendance_min = null;
      } else {
        cleanCriteria.attendance_min = parseInt(cleanCriteria.attendance_min, 10);
      }
      if (cleanCriteria.attendance_max === '' || cleanCriteria.attendance_max === null) {
        cleanCriteria.attendance_max = null;
      } else {
        cleanCriteria.attendance_max = parseInt(cleanCriteria.attendance_max, 10);
      }
      if (cleanCriteria.member_since_days_min === '' || cleanCriteria.member_since_days_min === null) {
        cleanCriteria.member_since_days_min = null;
      } else {
        cleanCriteria.member_since_days_min = parseInt(cleanCriteria.member_since_days_min, 10);
      }
      if (cleanCriteria.member_since_days_max === '' || cleanCriteria.member_since_days_max === null) {
        cleanCriteria.member_since_days_max = null;
      } else {
        cleanCriteria.member_since_days_max = parseInt(cleanCriteria.member_since_days_max, 10);
      }

      const response = await adminFetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || null,
          criteria: cleanCriteria
        })
      });

      if (response.ok) {
        showMessage(editing ? 'Segment updated' : 'Segment created');
        setShowEditor(false);
        fetchSegments();
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to save segment', 'error');
      }
    } catch (error) {
      console.error('Error saving segment:', error);
      showMessage('Failed to save segment', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (segment) => {
    if (!window.confirm(`Are you sure you want to delete "${segment.name}"?`)) {
      return;
    }

    try {
      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/segments/${segment.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        showMessage('Segment deleted');
        fetchSegments();
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to delete segment', 'error');
      }
    } catch (error) {
      console.error('Error deleting segment:', error);
      showMessage('Failed to delete segment', 'error');
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      // Clean up criteria for preview
      const cleanCriteria = { ...formData.criteria };
      if (cleanCriteria.attendance_min === '' || cleanCriteria.attendance_min === null) {
        cleanCriteria.attendance_min = null;
      } else {
        cleanCriteria.attendance_min = parseInt(cleanCriteria.attendance_min, 10);
      }
      if (cleanCriteria.attendance_max === '' || cleanCriteria.attendance_max === null) {
        cleanCriteria.attendance_max = null;
      } else {
        cleanCriteria.attendance_max = parseInt(cleanCriteria.attendance_max, 10);
      }
      if (cleanCriteria.member_since_days_min === '' || cleanCriteria.member_since_days_min === null) {
        cleanCriteria.member_since_days_min = null;
      } else {
        cleanCriteria.member_since_days_min = parseInt(cleanCriteria.member_since_days_min, 10);
      }
      if (cleanCriteria.member_since_days_max === '' || cleanCriteria.member_since_days_max === null) {
        cleanCriteria.member_since_days_max = null;
      } else {
        cleanCriteria.member_since_days_max = parseInt(cleanCriteria.member_since_days_max, 10);
      }

      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/segments/preview-criteria`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(cleanCriteria)
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewData(data);
      } else {
        showMessage('Failed to preview segment', 'error');
      }
    } catch (error) {
      console.error('Error previewing segment:', error);
      showMessage('Failed to preview segment', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const toggleArrayValue = (field, value) => {
    const current = formData.criteria[field];
    const newValue = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    setFormData({
      ...formData,
      criteria: { ...formData.criteria, [field]: newValue }
    });
  };

  const updateCriteria = (field, value) => {
    setFormData({
      ...formData,
      criteria: { ...formData.criteria, [field]: value }
    });
  };

  // Editor View
  if (showEditor) {
    return (
      <div className="space-y-6">
        {/* Editor Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => setShowEditor(false)}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePreview} disabled={previewing}>
              {previewing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
              Preview ({previewData?.total_count ?? '?'})
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Segment
            </Button>
          </div>
        </div>

        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Segment Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Active Adult Students"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional description..."
            />
          </div>
        </div>

        {/* Criteria Builder */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Segment Criteria
            </CardTitle>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Recipients must match ALL selected criteria (AND logic)
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Program Filter */}
            <div className="space-y-2">
              <Label>Programs (Include)</Label>
              <div className="flex flex-wrap gap-2">
                {programs.map(program => (
                  <label
                    key={program.id}
                    className={`px-3 py-1.5 rounded-full text-sm cursor-pointer transition-colors ${
                      formData.criteria.programs.includes(program.id)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={formData.criteria.programs.includes(program.id)}
                      onChange={() => toggleArrayValue('programs', program.id)}
                    />
                    {program.name}
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Leave empty to include all programs</p>
            </div>

            {/* Belt Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Minimum Belt</Label>
                <select
                  value={formData.criteria.belt_min}
                  onChange={(e) => updateCriteria('belt_min', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground"
                >
                  <option value="">Any</option>
                  {belts.map(belt => (
                    <option key={belt.id} value={belt.name}>{belt.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Maximum Belt</Label>
                <select
                  value={formData.criteria.belt_max}
                  onChange={(e) => updateCriteria('belt_max', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground"
                >
                  <option value="">Any</option>
                  {belts.map(belt => (
                    <option key={belt.id} value={belt.name}>{belt.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Attendance Filter */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Classes (Last 30 Days)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.criteria.attendance_min ?? ''}
                  onChange={(e) => updateCriteria('attendance_min', e.target.value)}
                  placeholder="Any"
                />
              </div>
              <div className="space-y-2">
                <Label>Max Classes (Last 30 Days)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.criteria.attendance_max ?? ''}
                  onChange={(e) => updateCriteria('attendance_max', e.target.value)}
                  placeholder="Any"
                />
              </div>
            </div>

            {/* Membership Status */}
            <div className="space-y-2">
              <Label>Membership Status</Label>
              <div className="flex flex-wrap gap-2">
                {MEMBERSHIP_STATUSES.map(status => (
                  <label
                    key={status.value}
                    className={`px-3 py-1.5 rounded-full text-sm cursor-pointer transition-colors ${
                      formData.criteria.membership_status.includes(status.value)
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={formData.criteria.membership_status.includes(status.value)}
                      onChange={() => toggleArrayValue('membership_status', status.value)}
                    />
                    {status.label}
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Leave empty to include all statuses</p>
            </div>

            {/* Member Tenure */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Member for at least (days)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.criteria.member_since_days_min ?? ''}
                  onChange={(e) => updateCriteria('member_since_days_min', e.target.value)}
                  placeholder="Any"
                />
              </div>
              <div className="space-y-2">
                <Label>Member for at most (days)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.criteria.member_since_days_max ?? ''}
                  onChange={(e) => updateCriteria('member_since_days_max', e.target.value)}
                  placeholder="Any"
                />
              </div>
            </div>

            {/* Account Type */}
            <div className="space-y-2">
              <Label>Account Type</Label>
              <div className="flex flex-wrap gap-2">
                {ACCOUNT_TYPES.map(type => (
                  <label
                    key={type.value}
                    className={`px-3 py-1.5 rounded-full text-sm cursor-pointer transition-colors ${
                      formData.criteria.account_types.includes(type.value)
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={formData.criteria.account_types.includes(type.value)}
                      onChange={() => toggleArrayValue('account_types', type.value)}
                    />
                    {type.label}
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Leave empty to include all account types</p>
            </div>
          </CardContent>
        </Card>

        {/* Preview Results */}
        {previewData && (
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-600" />
                Preview: {previewData.total_count} recipient{previewData.total_count !== 1 ? 's' : ''} match
              </CardTitle>
            </CardHeader>
            {previewData.sample_recipients.length > 0 && (
              <CardContent>
                <p className="text-sm text-gray-600 mb-2">Sample recipients:</p>
                <div className="space-y-1">
                  {previewData.sample_recipients.map(recipient => (
                    <div key={recipient.id} className="text-sm">
                      <span className="font-medium">{recipient.name || 'No name'}</span>
                      <span className="text-gray-500 ml-2">{recipient.email}</span>
                    </div>
                  ))}
                  {previewData.total_count > 10 && (
                    <p className="text-xs text-gray-400 mt-2">
                      ...and {previewData.total_count - 10} more
                    </p>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        )}
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Create dynamic segments to target newsletters based on member attributes.
          Segments automatically update as members change.
        </p>
        <Button onClick={handleCreate} variant="adminPrimary">
          <Plus className="h-4 w-4 mr-2" />
          New Segment
        </Button>
      </div>

      {/* Segments List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : segments.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Filter className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>No segments yet</p>
          <p className="text-sm">Create a segment to target newsletters based on member attributes</p>
        </div>
      ) : (
        <div className="space-y-3">
          {segments.map((segment) => (
            <Card key={segment.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900 dark:text-gray-100">{segment.name}</h3>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        <Users className="h-3 w-3" />
                        {segment.recipient_count} recipients
                      </span>
                    </div>
                    {segment.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{segment.description}</p>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {segment.criteria?.programs?.length > 0 && (
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                          {segment.criteria.programs.length} program(s)
                        </span>
                      )}
                      {(segment.criteria?.belt_min || segment.criteria?.belt_max) && (
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                          Belt: {segment.criteria.belt_min || 'Any'} - {segment.criteria.belt_max || 'Any'}
                        </span>
                      )}
                      {segment.criteria?.membership_status?.length > 0 && (
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                          {segment.criteria.membership_status.join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(segment)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(segment)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
